import * as j from '@mojotech/json-type-validation';

export const AgentState = {
  LoggedIn: 'Logged In',
  Initialised: 'Initialised',
  // Ringing Agent dialler for login
  Ringing: 'Ringing',
  Unavailable: 'Unavailable',
  CheckingAssignments: 'Checking Assignments',
  InQueue: 'In Queue',
  Preview: 'Preview',
  Connected: 'Connected',
  AfterCallWork: 'AfterCallWork',
  Disconnected: 'Disconnected',
} as const;

export type AgentState = typeof AgentState[keyof typeof AgentState];

export const ChannelAction = {
  Hold: 'hold',
  StopHold: 'stopHold',
  Mute: 'mute',
  Unmute: 'unmute',
  Hangup: 'hangup',
} as const;

export type ChannelAction = typeof ChannelAction[keyof typeof ChannelAction];

// Key is username
export type AgentStates = { [key: string]: string };

type EndpointAttributes = {
  type: string;
  attributes: { [key: string]: string };
};

type Lead = {
  leadId: number;
  externalId: string;
  callbackId: number | null;
  campaignId: string;
  campaignName: string;
  name: string;
  attributes: { [key: string]: string };
  endpointAttributes: { [key: string]: EndpointAttributes };
  requiredSkills: string;
  timezone: string;
  endpoints: string[];
  callbackNotes: string | null;
};

export type Disposition = {
  title: string;
  outcome: string;
  description: string;
  code: string;
  subCode: string;
};

export type DiallingDayTimes = {
  startTimeHour: number;
  startTimeMin: number;
  endTimeHour: number;
  endTimeMin: number;
};

export type DiallingHours = {
  diallingDays: { [key: string]: DiallingDayTimes };
  allowPublicHolidays: boolean;
};

type PublicHoliday = {
  year: number;
  month: number;
  day: number;
  name: string;
};

type Callback = {
  forAgent: string | null;
  scheduled: string;
  scheduledLocalTime: string;
  endpoint: string;
  notes: string;
};

type Exclusion = {
  list: string;
  endpoint: string;
};

type CallDisposition = {
  code: string;
  subCode: string;
  callback: Callback | null;
  exclusionList: string | null;
};

export type UpdateCallDisposition = Omit<CallDisposition, 'exclusionList'> & {
  exclusion: Exclusion | null;
  // Anonymous caller fields (customerNum === 0)
  leadName?: string;
  leadTimezone?: string;
  leadExternalId?: string;
};

type StateChange = {
  previousState: string;
  changeTime: string;
  state: string;
};

type AgentChannel = {
  key: string;
  stateHistory: StateChange[] | null;
};

export type AgentControl = {
  username: string;
  channel: AgentChannel | null;
};

export type CustomerChannel = {
  key: string;
  stateHistory: StateChange[] | null;
  isInbound: boolean;
  address: string;
  muted: boolean;
};

export type Call = {
  id: string;
  attemptId: number;
  initialContactId: string;
  isRecording: boolean;
  lead: Lead;
  dispositions: Disposition[];
  exclusionLists: string[];
  callbackDiallingHours: DiallingHours;
  callbackPublicHoliday: PublicHoliday[];
  disposition: CallDisposition | null;
  agentControls: { [key: string]: AgentControl };
  pendingAgentControls: { [key: string]: AgentControl };
  customerChannels: { [key: string]: CustomerChannel };
};

type AgentAsteriskAuthenticationEvent = {
  alreadyRegistered: boolean;
  asteriskUsername: string;
  asteriskPassword: string;
  asteriskHost: string;
};

type AgentOfferedConferenceEvent = {
  call: Call | null;
};

type AgentRejectedConferenceEvent = {
  call: Call | null;
};

type AgentEnteredStateEvent = {
  newState: AgentState;
  stateTime: string;
  username: string;
};

type AgentPendingStateEvent = {
  pendingState: AgentState;
};

type AgentStateChangeFailedEvent = {
  rejectedState: AgentState;
};

type AgentCallFailedEvent = {
  address: string;
};

type AgentPreviewCallEvent = {
  call: Call | null;
};

type CampaignInformationEvent = {
  agentStatuses: string[];
  description: string;
  name: string;
};

type AgentConnectionEvent = {
  muted: boolean;
};

export type ServerMessage = {
  asteriskAuthentication?: AgentAsteriskAuthenticationEvent;
  enteredState?: AgentEnteredStateEvent;
  pendingState?: AgentPendingStateEvent;
  offeredConference?: AgentOfferedConferenceEvent;
  rejectedConference?: AgentRejectedConferenceEvent;
  agentStates?: AgentStates;
  stateChangeFailed?: AgentStateChangeFailedEvent;
  callFailed?: AgentCallFailedEvent;
  callInformation?: Call;
  previewCallInformation?: AgentPreviewCallEvent;
  campaignInformation?: CampaignInformationEvent;
  agentConnection?: AgentConnectionEvent;
  exclusionLists?: string[]; // do not call back again
};

const AgentAsteriskAuthenticationEventDecoder: j.Decoder<AgentAsteriskAuthenticationEvent> = j.object({
  alreadyRegistered: j.boolean(),
  asteriskUsername: j.string(),
  asteriskPassword: j.string(),
  asteriskHost: j.string(),
});

const EndpointAttributesDecoder: j.Decoder<EndpointAttributes> = j.object({
  type: j.string(),
  attributes: j.dict(j.string()),
});

const LeadDecoder: j.Decoder<Lead> = j
  .object({
    lead_id: j.number(),
    external_id: j.string(),
    callback_id: j.union(j.number(), j.constant(null)),
    campaign_id: j.number(),
    campaign_name: j.string(),
    lead_name: j.string(),
    attributes: j.dict(j.string()),
    endpoint_attributes: j.dict(EndpointAttributesDecoder),
    required_skills: j.string(),
    timezone: j.string(),
    endpoints: j.array(j.string()),
    callback_notes: j.optional(j.union(j.string(), j.constant(null))),
  })
  .map((item) => ({
    leadId: item.lead_id,
    externalId: item.external_id,
    callbackId: item.callback_id,
    campaignId: String(item.campaign_id),
    campaignName: item.campaign_name,
    name: item.lead_name,
    attributes: item.attributes,
    endpointAttributes: item.endpoint_attributes,
    requiredSkills: item.required_skills,
    timezone: item.timezone,
    endpoints: item.endpoints,
    callbackNotes: item.callback_notes ?? null,
  }));

const DispositionDecoder: j.Decoder<Disposition> = j
  .object({
    title: j.string(),
    outcome: j.string(),
    description: j.string(),
    code: j.string(),
    sub_code: j.string(),
  })
  .map((item) => ({
    title: item.title,
    outcome: item.outcome,
    description: item.description,
    code: item.code,
    subCode: item.sub_code,
  }));

export const DiallingHoursDecoder: j.Decoder<DiallingHours> = j
  .object({
    allow_public_holidays: j.boolean(),
    dialling_days: j.dict(
      j.union(
        j.object({
          start_time_hour: j.number(),
          start_time_min: j.number(),
          end_time_hour: j.number(),
          end_time_min: j.number(),
        }),
        j.constant(null),
      ),
    ),
  })
  .map((item) => ({
    allowPublicHolidays: item.allow_public_holidays,
    diallingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].reduce((out, day) => {
      const hrs = item.dialling_days[day];
      return {
        ...out,
        [day]: hrs
          ? {
              startTimeHour: hrs.start_time_hour,
              startTimeMin: hrs.start_time_min,
              endTimeHour: hrs.end_time_hour,
              endTimeMin: hrs.end_time_min,
            }
          : null,
      };
    }, {}),
  }));

const PublicHolidayDecoder: j.Decoder<PublicHoliday> = j.object({
  year: j.number(),
  month: j.number(),
  day: j.number(),
  name: j.string(),
});

const CallbackDecoder: j.Decoder<Callback> = j
  .object({
    for_agent: j.union(j.string(), j.constant(null)),
    scheduled: j.string(),
    scheduled_local_time: j.string(),
    endpoint: j.string(),
    notes: j.string(),
  })
  .map((item) => ({
    forAgent: item.for_agent,
    scheduled: item.scheduled,
    scheduledLocalTime: item.scheduled_local_time,
    endpoint: item.endpoint,
    notes: item.notes,
  }));

const CallDispositionDecoder: j.Decoder<CallDisposition> = j
  .object({
    disposition_code: j.string(),
    disposition_sub_code: j.string(),
    callback: j.union(CallbackDecoder, j.constant(null)),
    exclusion_list: j.union(j.string(), j.constant(null)),
  })
  .map((item) => ({
    code: item.disposition_code,
    subCode: item.disposition_sub_code,
    callback: item.callback,
    exclusionList: item.exclusion_list,
  }));

const StateChangeDecoder: j.Decoder<StateChange> = j.object({
  previousState: j.string(),
  changeTime: j.string(),
  state: j.string(),
});

const AgentChannelDecoder: j.Decoder<AgentChannel> = j.object({
  key: j.string(),
  stateHistory: j.union(j.array(StateChangeDecoder), j.constant(null)),
});

const AgentControlDecoder: j.Decoder<AgentControl> = j.object({
  username: j.string(),
  channel: j.union(AgentChannelDecoder, j.constant(null)),
});

const CustomerChannelDecoder: j.Decoder<CustomerChannel> = j.object({
  key: j.string(),
  stateHistory: j.union(j.array(StateChangeDecoder), j.constant(null)),
  isInbound: j.boolean(),
  address: j.string(),
  muted: j.boolean(),
});

const CallDecoder: j.Decoder<Call> = j
  .object({
    id: j.string(),
    customerNum: j.number(),
    initialContactId: j.string(),
    isRecording: j.boolean(),
    lead: LeadDecoder,
    dispositions: j.array(DispositionDecoder),
    exclusionLists: j.union(j.array(j.string()), j.constant(null)),
    callbackDiallingHours: DiallingHoursDecoder,
    callbackPublicHoliday: j.union(j.array(PublicHolidayDecoder), j.constant(null)),
    disposition: j.union(CallDispositionDecoder, j.constant(null)),
    agentControls: j.dict(AgentControlDecoder),
    pendingAgentControls: j.dict(AgentControlDecoder),
    customerChannels: j.dict(CustomerChannelDecoder),
  })
  .map((item) => ({
    id: item.id,
    attemptId: item.customerNum,
    initialContactId: item.initialContactId,
    isRecording: item.isRecording,
    lead: item.lead,
    dispositions: item.dispositions,
    exclusionLists: item.exclusionLists ?? [],
    callbackDiallingHours: item.callbackDiallingHours,
    callbackPublicHoliday: item.callbackPublicHoliday ?? [],
    disposition: item.disposition,
    agentControls: item.agentControls,
    pendingAgentControls: item.pendingAgentControls,
    customerChannels: item.customerChannels,
  }));

const AgentOfferedConferenceEventDecoder: j.Decoder<AgentOfferedConferenceEvent> = j.object({
  call: j.union(CallDecoder, j.constant(null)),
});

const AgentRejectedConferenceEventDecoder: j.Decoder<AgentRejectedConferenceEvent> = j.object({
  call: j.union(CallDecoder, j.constant(null)),
});

const AgentEnteredStateEventDecoder: j.Decoder<AgentEnteredStateEvent> = j.object({
  newState: j.string() as j.Decoder<AgentState>,
  stateTime: j.string(),
  username: j.string(),
});

const AgentPendingStateEventDecoder: j.Decoder<AgentPendingStateEvent> = j.object({
  pendingState: j.string() as j.Decoder<AgentState>,
});

const AgentStateChangeFailedEventDecoder: j.Decoder<AgentStateChangeFailedEvent> = j.object({
  rejectedState: j.string() as j.Decoder<AgentState>,
});

const AgentCallFailedEventDecoder: j.Decoder<AgentCallFailedEvent> = j.object({
  address: j.string(),
});

const AgentConnectionEventDecoder: j.Decoder<AgentConnectionEvent> = j.object({
  muted: j.boolean(),
});

const AgentPreviewCallEventDecoder: j.Decoder<AgentRejectedConferenceEvent> = j.object({
  call: j.union(CallDecoder, j.constant(null)),
});

const CampaignInformationEventDecoder: j.Decoder<CampaignInformationEvent> = j.object({
  agentStatuses: j.array(j.string()),
  description: j.string(),
  name: j.string(),
});

export const ServerMessageResponseDecoder: j.Decoder<ServerMessage> = j.object({
  asteriskAuthentication: j.optional(AgentAsteriskAuthenticationEventDecoder),
  enteredState: j.optional(AgentEnteredStateEventDecoder),
  pendingState: j.optional(AgentPendingStateEventDecoder),
  offeredConference: j.optional(AgentOfferedConferenceEventDecoder),
  rejectedConference: j.optional(AgentRejectedConferenceEventDecoder),
  agentStates: j.optional(j.dict(j.string())),
  stateChangeFailed: j.optional(AgentStateChangeFailedEventDecoder),
  callFailed: j.optional(AgentCallFailedEventDecoder),
  callInformation: j.optional(CallDecoder),
  previewCallInformation: j.optional(AgentPreviewCallEventDecoder),
  campaignInformation: j.optional(CampaignInformationEventDecoder),
  agentConnection: j.optional(AgentConnectionEventDecoder),
  exclusionLists: j.optional(j.array(j.string())),
});
